import datapoints from '../datapoints';
import openWindow from '../utils/openWindow';
import type WindowFeaturesOptions from '../store/schema/WindowFeaturesOptions';
import type { default as PopoutData, DeeplinkPopoutData } from '../store/schema/PopoutData';
import extractData from '../utils/extractData';
import getDeeplinkUrl from '../utils/getDeeplinkUrl';
import getProjectionUrl from '../utils/getProjectionUrl';
import { popoutLoaded } from '../utils/popoutLoaded';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports  -- (https://aka.ms/OWALintWiki)
 * Baseline. Do not copy and paste"
 *	> '../index' import is restricted from being used. */
import { lazyBootPopout } from '../index';
import { PerformanceDatapoint, wrapFunctionForDatapoint, DatapointStatus } from 'owa-analytics';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import type { vDirType } from 'owa-vdir-types';
import type { MailboxInfo } from 'owa-client-types';
/**
 * Open a new popout window.
 * A popout window can be a deeplink window or a projection window, determined by
 * 1. Browser supports projection or not
 * 2. Whether a tab id for projection tab is provided
 * When both are true, popout window will use projection technology to show content, which has better performance than deeplink.
 * @param vdir mail or calendar
 * @param route the route path of the popout window, e.g. 'compose', 'read'
 * @param data data for the popout. It can be just an object, or a callback returns an object, or a PopoutData object which contains data for both deeplink and projection
 * @param options: window options, such as width, height, ...
 * @param skipOptinCheck Whether skip optIn check. When set to true, a param "minus" will be added to the url actionSource: Source of this action
 * @param actionSource Source of this action
 * @param additionalUrlParameters additional scenario-based parameters to include in the generated URL
 */
const defaultExport: (
    mailboxInfo: MailboxInfo,
    _vdir: vDirType,
    route: string,
    _data?: object | (() => Promise<object>) | undefined,
    _options?: Partial<WindowFeaturesOptions> | undefined,
    _skipOptinCheck?: boolean | undefined,
    actionSource?: string | undefined,
    ...args: any[]
) => void = wrapFunctionForDatapoint(
    { name: 'PopoutAddPopout', ...datapoints.PopoutAddPopout, mailbox: mailboxInfo => mailboxInfo },
    function (
        mailboxInfo: MailboxInfo,
        vdir: vDirType,
        route: string,
        data?: DeeplinkPopoutData | PopoutData,
        options?: Partial<WindowFeaturesOptions>,
        skipOptinCheck?: boolean,
        _actionSource?: string,
        additionalUrlParameters?: Record<string, string>
    ) {
        const {
            deeplinkCallback,
            projectionTabId,
            projectionTargetWindow,
            projectionParentWindow,
        } = extractData(data);

        const isProjection = !!projectionTabId && !isHostAppFeatureEnabled('platformAppSdk');

        const datapoint = isProjection
            ? new PerformanceDatapoint('PopoutProjectionPerf', mailboxInfo)
            : new PerformanceDatapoint('PopoutDeeplinkPerf', mailboxInfo);
        datapoint.addCustomData({ vdir, route });

        const popoutUrl = isProjection
            ? getProjectionUrl(
                  projectionTabId,
                  additionalUrlParameters && additionalUrlParameters['freezeDryId']
              )
            : getDeeplinkUrl(vdir, route, {
                  skipOptinCheck,
                  urlParameters: additionalUrlParameters,
              });
        const childWindow = projectionTargetWindow
            ? projectionTargetWindow
            : openWindow(popoutUrl, options, projectionParentWindow);
        datapoint.addToCustomWaterfall(1, 'WindowOpened');
        if (isProjection && !projectionTargetWindow) {
            // popoutLoaded call into native host is for projections loading into a newly created WebView2.
            // It allows the OutlookWebHost to perform some WebView2 initialization very early in the loading process
            popoutLoaded(projectionTabId);
        }

        if (childWindow) {
            lazyBootPopout
                .import()
                .then(bootPopout =>
                    bootPopout(
                        vdir,
                        childWindow,
                        projectionTabId,
                        deeplinkCallback,
                        datapoint,
                        mailboxInfo
                    )
                )
                .catch(error => {
                    const errorObj = error as Error;
                    datapoint.endWithError(DatapointStatus.ClientError, errorObj);
                });
        } else {
            datapoint.endWithError(
                DatapointStatus.ClientError,
                new Error('Failed to open popout window - no child window')
            );
        }
    }
);
export default defaultExport;
